import { useState, useMemo } from 'react';
import {
  useTransactions,
  useCategories,
  usePaymentMethods,
  useExchangeRatesList,
  useActiveRate,
  useBaseCurrency,
} from '../db';
import { useSyncStore } from '../stores';
import { dayjs, formatPeriod, parseDBDate } from '../lib/dayjs';
import { Coins, TrendingUp, TrendingDown, AlertCircle, Receipt } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { CustomSelect } from '../components/common/CustomSelect';
import { EmptyState } from '../components/common/EmptyState';

const COLORS = ['#10B981', '#FF6900', '#3B82F6', '#8B5CF6', '#F59E0B', '#6B7280', '#EC4899', '#06B6D4'];

export function Dashboard() {
  const [period, setPeriod] = useState(() => dayjs().format('YYYY-MM'));

  // Database hooks
  const { transactions: incomes, isLoading: isIncomesLoading } = useTransactions({ type: 'income', period });
  const { transactions: expenses, isLoading: isExpensesLoading } = useTransactions({ type: 'expense', period });
  const isTxLoading = isIncomesLoading || isExpensesLoading;
  const { categories } = useCategories();
  const { paymentMethods } = usePaymentMethods();
  const { rates, isLoading: isRatesLoading } = useExchangeRatesList();
  const { baseCurrency, format } = useBaseCurrency();

  // Sync / Online state
  const isOnline = useSyncStore((s) => s.isOnline);
  const isSyncing = useSyncStore((s) => s.isSyncing);
  const pendingCount = useSyncStore((s) => s.pendingCount);

  // Get active exchange rates using memoized custom hook
  const { activeRate: latestRateDoc, isRateExpired } = useActiveRate();

  // Month select options memoized to prevent CustomSelect re-renders
  const periodOptions = useMemo(() => {
    const options = [];
    const now = dayjs();
    for (let i = 0; i < 6; i++) {
      const p = now.subtract(i, 'month').format('YYYY-MM');
      options.push({ value: p, label: formatPeriod(p) });
    }
    return options;
  }, []);

  // Helper to compute equivalent in base currency
  const getEquivalent = (tx: any) => tx.total_amount || 0;

  // Memoized math & chart calculations to eliminate un-memoized render lag
  const {
    totalIncomesLocal,
    totalExpensesLocal,
    totalIncomesEquiv,
    totalExpensesEquiv,
    netBalanceEquiv,
    dailyChartData,
    incomePieData,
    expensePieData,
    walletChartData,
  } = useMemo(() => {
    const totalIncomesLocal = incomes.reduce((sum, t) => sum + (t.total_amount || 0), 0);
    const totalExpensesLocal = expenses.reduce((sum, t) => sum + (t.total_amount || 0), 0);
    const totalIncomesEquiv = incomes.reduce((sum, t) => sum + getEquivalent(t), 0);
    const totalExpensesEquiv = expenses.reduce((sum, t) => sum + getEquivalent(t), 0);
    const netBalanceEquiv = totalIncomesEquiv - totalExpensesEquiv;

    // Fast Map for O(1) Category & Payment Method Lookups
    const catMap = new Map(categories.map((c) => [c.id, c.name]));
    const payMap = new Map(paymentMethods.map((m) => [m.id, m.name]));

    // --- Chart 1: Cashflow daily progress ---
    const dailyDataMap: Record<string, { date: string; Ingresos: number; Egresos: number }> = {};
    incomes.forEach((tx) => {
      const dayLabel = parseDBDate(tx.date).format('DD/MM');
      if (!dailyDataMap[dayLabel]) dailyDataMap[dayLabel] = { date: dayLabel, Ingresos: 0, Egresos: 0 };
      dailyDataMap[dayLabel].Ingresos += getEquivalent(tx);
    });
    expenses.forEach((tx) => {
      const dayLabel = parseDBDate(tx.date).format('DD/MM');
      if (!dailyDataMap[dayLabel]) dailyDataMap[dayLabel] = { date: dayLabel, Ingresos: 0, Egresos: 0 };
      dailyDataMap[dayLabel].Egresos += getEquivalent(tx);
    });

    const dailyChartData = Object.values(dailyDataMap).sort((a, b) => a.date.localeCompare(b.date));

    // --- Chart 2: Incomes by Category ---
    const incomeCategoryMap: Record<string, number> = {};
    incomes.forEach((tx) => {
      (tx.items || []).forEach((item: any) => {
        const name = catMap.get(item.category_id) || 'Sin categoría';
        incomeCategoryMap[name] = (incomeCategoryMap[name] || 0) + (item.net_amount || 0);
      });
    });

    const incomePieData = Object.entries(incomeCategoryMap).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2)),
    }));

    // --- Chart 3: Expenses by Category ---
    const expenseCategoryMap: Record<string, number> = {};
    expenses.forEach((tx) => {
      (tx.items || []).forEach((item: any) => {
        const name = catMap.get(item.category_id) || 'Sin categoría';
        expenseCategoryMap[name] = (expenseCategoryMap[name] || 0) + (item.net_amount || 0);
      });
    });

    const expensePieData = Object.entries(expenseCategoryMap).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2)),
    }));

    // --- Chart 4: Balance by Payment Method / Wallet ---
    const walletMap: Record<string, number> = {};
    incomes.forEach((tx) => {
      (tx.payments || []).forEach((p: any) => {
        const name = payMap.get(p.payment_method_id) || 'Otro';
        const val = p.base_equivalent ?? p.received_amount ?? 0;
        walletMap[name] = (walletMap[name] || 0) + val;
      });
    });
    expenses.forEach((tx) => {
      (tx.payments || []).forEach((p: any) => {
        const name = payMap.get(p.payment_method_id) || 'Otro';
        const val = p.base_equivalent ?? p.received_amount ?? 0;
        walletMap[name] = (walletMap[name] || 0) - val;
      });
    });

    const walletChartData = Object.entries(walletMap).map(([name, balance]) => ({
      name,
      balance: parseFloat(balance.toFixed(2)),
    }));

    return {
      totalIncomesLocal,
      totalExpensesLocal,
      totalIncomesEquiv,
      totalExpensesEquiv,
      netBalanceEquiv,
      dailyChartData,
      incomePieData,
      expensePieData,
      walletChartData,
    };
  }, [incomes, expenses, categories, paymentMethods]);

  const hasNoTransactions = !isTxLoading && incomes.length === 0 && expenses.length === 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-text-primary tracking-tight">Dashboard</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Métricas clave y status de la contabilidad
          </p>
        </div>
        <CustomSelect
          value={period}
          onChange={(val) => setPeriod(val)}
          options={periodOptions}
          className="w-full sm:w-48"
        />
      </div>

      {/* Rate Warning Banner */}
      {isRateExpired && !isRatesLoading && (
        <div className="flex items-center gap-3 rounded-xl border border-warning/30 bg-warning/10 p-3.5 text-xs text-warning font-semibold shadow-xs">
          <AlertCircle className="h-5 w-5 text-warning shrink-0 animate-pulse" />
          <div>
            <span className="block font-bold">Tasa cambiaria no disponible o vencida</span>
            <p className="mt-0.5 text-[11px] opacity-90 leading-relaxed font-normal">
              {(() => {
                const formatDateStr = (str?: string) => {
                  if (!str) return 'No disponible';
                  const parts = str.substring(0, 10).split('-');
                  if (parts.length !== 3) return str;
                  return `${parts[2]}/${parts[1]}/${parts[0]}`;
                };
                return latestRateDoc 
                  ? `La última tasa registrada (vigente desde ${formatDateStr(latestRateDoc.since)}) venció el ${formatDateStr(latestRateDoc.until)}. Buscando actualización...`
                  : 'No se encontraron tasas de cambio registradas en el sistema. Buscando actualización...';
              })()}
            </p>
          </div>
        </div>
      )}

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {/* KPI 1: Net flow */}
        <div className="group rounded-2xl border border-border bg-surface p-4 shadow-xs hover:shadow-md hover:border-brand-sky/40 transition-all duration-200 active:scale-[0.99]">
          <div className="mb-2 flex items-center justify-between">
            <Coins className="h-5 w-5 text-success" />
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${netBalanceEquiv >= 0 ? 'bg-success/15 text-success' : 'bg-error/15 text-error'}`}>
              Consolidado
            </span>
          </div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Flujo Neto</p>
          <p className={`mt-1 text-lg font-extrabold font-mono ${netBalanceEquiv >= 0 ? 'text-success' : 'text-error'}`}>
            {isTxLoading ? '...' : format(netBalanceEquiv)}
          </p>
          <p className="text-[10px] text-text-tertiary mt-0.5 font-medium">Equivalente base</p>
        </div>

        {/* KPI 2: Incomes */}
        <div className="group rounded-2xl border border-border bg-surface p-4 shadow-xs hover:shadow-md hover:border-brand-sky/40 transition-all duration-200 active:scale-[0.99]">
          <div className="mb-2 flex items-center justify-between">
            <TrendingUp className="h-5 w-5 text-success" />
            <div className="h-2 w-2 rounded-full bg-success shadow-glow" />
          </div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Ingresos</p>
          <p className="mt-1 text-lg font-extrabold text-text-primary font-mono">
            {isTxLoading ? '...' : format(totalIncomesEquiv)}
          </p>
          <p className="text-[10px] text-text-tertiary mt-0.5 font-mono">Bs. {totalIncomesLocal.toLocaleString('es-VE')}</p>
        </div>

        {/* KPI 3: Expenses */}
        <div className="group rounded-2xl border border-border bg-surface p-4 shadow-xs hover:shadow-md hover:border-brand-sky/40 transition-all duration-200 active:scale-[0.99]">
          <div className="mb-2 flex items-center justify-between">
            <TrendingDown className="h-5 w-5 text-error" />
            <div className="h-2 w-2 rounded-full bg-error" />
          </div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Egresos</p>
          <p className="mt-1 text-lg font-extrabold text-text-primary font-mono">
            {isTxLoading ? '...' : format(totalExpensesEquiv)}
          </p>
          <p className="text-[10px] text-text-tertiary mt-0.5 font-mono">Bs. {totalExpensesLocal.toLocaleString('es-VE')}</p>
        </div>

        {/* KPI 4: Total Incomes Count */}
        <div className="group rounded-2xl border border-border bg-surface p-4 shadow-xs hover:shadow-md hover:border-brand-sky/40 transition-all duration-200 active:scale-[0.99]">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold text-brand-sky bg-brand-sky/10 px-2 py-0.5 rounded-full">Operaciones</span>
          </div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Cant. Ingresos</p>
          <p className="mt-1 text-lg font-extrabold text-text-primary font-mono">{isTxLoading ? '...' : incomes.length}</p>
          <p className="text-[10px] text-text-tertiary mt-0.5 font-medium">En el período</p>
        </div>

        {/* KPI 5: Total Expenses Count */}
        <div className="group rounded-2xl border border-border bg-surface p-4 shadow-xs hover:shadow-md hover:border-brand-sky/40 transition-all duration-200 active:scale-[0.99]">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-bold text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full">Gastos</span>
          </div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Cant. Egresos</p>
          <p className="mt-1 text-lg font-extrabold text-text-primary font-mono">{isTxLoading ? '...' : expenses.length}</p>
          <p className="text-[10px] text-text-tertiary mt-0.5 font-medium">En el período</p>
        </div>

        {/* KPI 6: Sync status */}
        <div className="group rounded-2xl border border-border bg-surface p-4 shadow-xs hover:shadow-md hover:border-brand-sky/40 transition-all duration-200 active:scale-[0.99]">
          <div className="mb-2 flex items-center justify-between">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${pendingCount > 0 ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}`}>
              {pendingCount > 0 ? 'Pendiente' : 'Al día'}
            </span>
          </div>
          <p className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">Sincronización</p>
          <p className="mt-1 text-lg font-extrabold text-text-primary font-mono">
            {pendingCount > 0 ? `${pendingCount} pend.` : '100%'}
          </p>
          <p className="text-[10px] text-text-tertiary mt-0.5 font-medium">{isOnline ? 'Conectado a la nube' : 'Modo Offline'}</p>
        </div>
      </div>

      {/* Main Content / EmptyState vs Charts */}
      {hasNoTransactions ? (
        <EmptyState
          type="empty"
          icon={Receipt}
          title="No hay transactions registradas en este período"
          description={`No se encontraron ingresos ni egresos contables registrados durante ${formatPeriod(period)}.`}
        />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Chart 1: Cashflow by day */}
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs flex flex-col gap-4">
            <div>
              <h3 className="font-bold text-sm text-text-primary">Evolución de Flujo de Caja</h3>
              <p className="text-xs text-text-secondary mt-0.5">Ingresos y egresos acumulados por día ({baseCurrency})</p>
            </div>
            <div className="h-64 w-full">
              {dailyChartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-xs text-text-tertiary">
                  Sin movimientos diarios en este período
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Ingresos" fill="#10B981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Egresos" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Chart 2: Balance by Payment Method */}
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs flex flex-col gap-4">
            <div>
              <h3 className="font-bold text-sm text-text-primary">Balance por Caja/Billetera</h3>
              <p className="text-xs text-text-secondary mt-0.5">Saldo neto acumulado por método de pago ({baseCurrency})</p>
            </div>
            <div className="h-64 w-full">
              {walletChartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-xs text-text-tertiary">
                  Sin movimientos financieros para procesar
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={walletChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                    <YAxis tick={{ fontSize: 9 }} stroke="#94a3b8" />
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="balance" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                      {walletChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.balance >= 0 ? '#10B981' : '#EF4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Chart 3: Income distribution */}
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs flex flex-col gap-4">
            <div>
              <h3 className="font-bold text-sm text-text-primary">Distribución de Ingresos</h3>
              <p className="text-xs text-text-secondary mt-0.5">Porcentaje de ingresos por categoría</p>
            </div>
            <div className="h-64 w-full flex items-center justify-center">
              {incomePieData.length === 0 ? (
                <div className="text-xs text-text-tertiary">No hay ingresos registrados en este mes</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={incomePieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {incomePieData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Chart 4: Expense distribution */}
          <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs flex flex-col gap-4">
            <div>
              <h3 className="font-bold text-sm text-text-primary">Distribución de Egresos</h3>
              <p className="text-xs text-text-secondary mt-0.5">Porcentaje de gastos por categoría</p>
            </div>
            <div className="h-64 w-full flex items-center justify-center">
              {expensePieData.length === 0 ? (
                <div className="text-xs text-text-tertiary">No hay egresos registrados en este mes</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expensePieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {expensePieData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip wrapperStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
